import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import gql from 'graphql-tag';
import injectSheet from 'react-jss';
import classnames from 'classnames';

// React Window
import { FixedSizeList as List } from 'react-window';

import TraceIconSpinner from 'components/ui/traceIconSpinner';

import GroupsTaskListRow from './groupsTaskListRow';
import GroupsTaskListItem, {
  fragments as groupsTaskListItemFragments
} from './groupsTaskListItem';
import GroupTaskListHeader from './groupTaskListHeader';

import styles from './groupsTaskList.style';

// some constants specific to groups task list
const DEFAULT_ORDER_BY = 'ALL_DESC';
const TASKLIST_PAGINATION_SIZE = 20;
const TASKLIST_PAGINATION_BUFFER = 10;

const getSortFromConfig = config => config.orderBy || DEFAULT_ORDER_BY;

const getFilterFromConfig = config => {
  const { filterWorkflowId, filterGroupId } = config;
  return {
    workflowId: { equalTo: filterWorkflowId },
    groupId: { equalTo: filterGroupId }
  };
};

// 'member' functions

// load more priorities items from pagination
const onLoadMoreFn = (workflows, prioritiesQuery, config, fetchMorePromise) => {
  // If a fetch more query is in progress, do not try to load
  if (fetchMorePromise.current) return;

  const {
    priorities: { pageInfo: { endCursor } = {} } = {},
    fetchMore: fetchMorePriorities
  } = prioritiesQuery;

  // fetchMore will only fetch more priorities
  const filter = getFilterFromConfig(config);
  const orderBy = getSortFromConfig(config);

  fetchMorePromise.current = fetchMorePriorities({
    query: queries.prioritiesQuery,
    variables: {
      first: TASKLIST_PAGINATION_SIZE,
      cursor: endCursor,
      filter,
      orderBy
    },
    updateQuery: (previousResult, { fetchMoreResult }) => {
      const previousPriorities = previousResult.priorities;
      const newPriorities = fetchMoreResult.priorities;

      const {
        pageInfo: previousPageInfo,
        nodes: previousPrioritiesNodes
      } = previousPriorities;
      const {
        pageInfo: newPageInfo,
        nodes: newPrioritiesNodes
      } = newPriorities;

      return {
        priorities: {
          ...previousPriorities,
          // By updating page info with the `cursor` here, we update `fetchMore` function
          pageInfo: {
            ...previousPageInfo,
            ...newPageInfo
          },
          nodes: [
            // Put the new priorities after the previous ones
            ...previousPrioritiesNodes,
            ...newPrioritiesNodes
          ]
        }
      };
    }
  }).then(() => {
    fetchMorePromise.current = null;
  });
};

// groups task list of the user dashboard
export const GroupsTaskList = React.memo(
  ({ classes, className, workflows, prioritiesQuery, config, setConfig }) => {
    const fetchMorePromise = useRef(null);

    // state for refetching priorities when filterOwnerId changes
    const [refetching, setRefetching] = useState(false);

    const {
      loading: prioritiesQueryLoading,
      priorities,
      refetch: refetchPriorities
    } = prioritiesQuery;

    const {
      totalCount = 0,
      pageInfo: { hasNextPage = false } = {},
      nodes: prioritiesList = []
    } = priorities || {};

    // set filter workflow and group id
    const { filterWorkflowId, filterGroupId } = config;

    const filterWorkflow = workflows.find(w => w.rowId === filterWorkflowId);
    const filterGroup =
      filterWorkflow &&
      filterWorkflow.groups.nodes.find(g => g.rowId === filterGroupId);

    // pagination of priorities query
    const pagination = {
      totalCount,
      buffer: TASKLIST_PAGINATION_BUFFER,
      hasNextPage,
      onLoadMore: () =>
        onLoadMoreFn(workflows, prioritiesQuery, config, fetchMorePromise)
    };

    // component to display the priorities list for the chosen group (or for all)
    // if no element or loading we show a specific message
    // if no more than 5 items we show them in full
    // if more than 5 items we use a virtualization window
    let prioritiesListContent;
    switch (true) {
      case refetching || (prioritiesQueryLoading && !priorities):
        prioritiesListContent = (
          <div className={classes.listInfoContainer}>
            <TraceIconSpinner className={classes.loadingListIcon} small />
          </div>
        );
        break;
      case totalCount === 0:
        prioritiesListContent = (
          <div className={classes.listInfoContainer}>
            <div className={classes.emptyListMessage}>
              {filterGroup ? (
                <>
                  No trace awaiting{' '}
                  <b className={classes.emptyListMessageGroupName}>
                    {filterGroup.name}
                  </b>{' '}
                  action
                </>
              ) : (
                'No trace awaiting your action'
              )}
            </div>
          </div>
        );
        break;
      case totalCount <= 5:
        prioritiesListContent = prioritiesList.map(priority => (
          <GroupsTaskListItem
            key={priority.id}
            priority={priority}
            displayGroupInfo={!filterGroupId}
          />
        ));
        break;
      default:
        prioritiesListContent = (
          <List
            className="reactWindowList"
            height={775}
            itemCount={prioritiesList.length}
            itemSize={155}
            itemData={{
              priorities: prioritiesList,
              pagination,
              displayGroupInfo: !filterGroupId
            }}
          >
            {GroupsTaskListRow}
          </List>
        );
    }

    return (
      <div className={classnames(className, classes.container)}>
        <GroupTaskListHeader
          workflows={workflows}
          setConfig={setConfig}
          config={config}
          setRefetching={setRefetching}
          refetchPriorities={refetchPriorities}
        />
        <div className={classes.listContainer}>{prioritiesListContent}</div>
      </div>
    );
  }
);

GroupsTaskList.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  workflows: PropTypes.array,
  prioritiesQuery: PropTypes.object.isRequired,
  config: PropTypes.object,
  setConfig: PropTypes.func.isRequired
};
GroupsTaskList.defaultProps = {
  className: '',
  workflows: [],
  config: {}
};

export const queries = {
  prioritiesQuery: gql`
    query prioritiesQuery(
      $first: Int
      $cursor: Cursor
      $orderBy: [PrioritiesOrderBy!]
      $filter: PriorityFilter
    ) {
      priorities(
        first: $first
        after: $cursor
        orderBy: $orderBy
        filter: $filter
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          ...PriorityFragment
        }
      }
    }
    ${groupsTaskListItemFragments.priority}
  `
};

export default compose(
  graphql(queries.prioritiesQuery, {
    name: 'prioritiesQuery',
    options: ({ workflows, config = {} }) => {
      const filter = getFilterFromConfig(config, workflows);
      const orderBy = getSortFromConfig(config);
      return {
        variables: {
          first: TASKLIST_PAGINATION_SIZE,
          orderBy,
          filter
        },
        fetchPolicy: 'cache-and-network'
      };
    }
  }),
  injectSheet(styles)
)(GroupsTaskList);
